import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, formatDateId } from '../api/client';
import type { ReceiptSummary } from '../api/types';
import { Alert, Card, Input, Spinner } from '../components/ui';
import GrnPrint from '../components/GrnPrint';
import ReceiptDetailModal from '../components/ReceiptDetailModal';
import { useT } from '../i18n/locale';
import { RECEIPT_STATUS_LABEL, ReceiptStatus } from '../api/enums';

export default function HistoryScreen() {
  const t = useT();
  const { databaseId } = useParams<{ databaseId: string }>();
  const [searchParams] = useSearchParams();
  const justPosted = searchParams.get('posted');

  const [receipts, setReceipts] = useState<ReceiptSummary[]>([]);
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [printId, setPrintId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  /** Bumped after a void so the list re-reads the new status. */
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!databaseId) return;
    const timer = window.setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ databaseId });
      if (search.trim()) params.set('search', search.trim());
      if (month) params.set('month', month);

      api
        .get<{ receipts: ReceiptSummary[] }>(`/receipts?${params}`)
        .then((d) => {
          setReceipts(d.receipts);
          setError(null);
        })
        .catch((e) => setError(e instanceof ApiError ? e.message : t('history.loadFailed')))
        .finally(() => setLoading(false));
      // Debounced so typing in the search box does not hammer the API.
    }, 250);

    return () => window.clearTimeout(timer);
  }, [databaseId, search, month, reloadToken]);

  return (
    <div className="mx-auto w-full max-w-7xl p-4">
      {justPosted ? (
        <div className="mb-3">
          <Alert tone="success">
            {t('history.justPostedPre')}
            <b>{justPosted}</b>
            {t('history.justPostedPost')}
          </Alert>
        </div>
      ) : null}

      <Card className="mb-3 flex flex-col gap-3 p-3 md:flex-row md:items-end">
        <div className="flex-1">
          <label className="mb-1 block text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            {t('history.search')}
          </label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('history.searchPlaceholder')}
          />
        </div>
        <div className="w-full md:w-56">
          <label className="mb-1 block text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            {t('history.monthFilter')}
          </label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
      </Card>

      {error ? <Alert tone="error">{error}</Alert> : null}

      <Card className="overflow-hidden">
        {loading ? (
          <Spinner label={t('history.loading')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead className="border-b border-slate-200 bg-slate-100/60 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                <tr>
                  <th className="w-12 px-2 py-2 text-center">No</th>
                  <th className="px-2 py-2">{t('history.colDate')}</th>
                  <th className="px-2 py-2">{t('history.colReceipt')}</th>
                  <th className="px-2 py-2">{t('history.colVendor')}</th>
                  <th className="px-2 py-2">{t('history.colDeliveryNote')}</th>
                  <th className="px-2 py-2">{t('history.colWarehouse')}</th>
                  <th className="px-2 py-2">{t('history.colCreatedBy')}</th>
                  <th className="px-2 py-2 text-center">{t('history.colItems')}</th>
                  <th className="px-2 py-2">{t('history.colStatus')}</th>
                  <th className="w-24 px-2 py-2 text-right">{t('history.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {receipts.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-10 text-center text-sm font-bold text-slate-400">
                      {t('history.empty')}
                    </td>
                  </tr>
                ) : (
                  receipts.map((r, i) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-blue-50/40">
                      <td className="px-2 py-1.5 text-center text-xs font-bold text-slate-600">{i + 1}</td>
                      <td className="px-2 py-1.5 text-xs font-semibold text-slate-800">
                        {formatDateId(r.date)}
                      </td>
                      <td className="px-2 py-1.5 text-xs font-bold text-teal-700">{r.grNumber}</td>
                      <td className="max-w-48 truncate px-2 py-1.5 text-xs font-bold text-slate-900" title={r.vendorName ?? ''}>
                        {r.vendorName ?? '-'}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-slate-600">{r.deliveryNoteNo}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-600">{r.warehouseName}</td>
                      <td
                        className="max-w-32 truncate px-2 py-1.5 text-xs text-slate-600"
                        title={r.createdByName}
                      >
                        {r.createdByName}
                      </td>
                      <td className="px-2 py-1.5 text-center text-xs font-bold text-slate-800">
                        {r.lineCount} <span className="text-slate-500">({r.totalQty})</span>
                      </td>
                      <td className="px-2 py-1.5">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setDetailId(r.id)}
                            title={t('history.viewDetail')}
                            aria-label={t('history.viewDetailFor', { number: r.grNumber })}
                            className="h-7 w-7 cursor-pointer rounded border border-slate-200 bg-slate-50 text-xs text-slate-600 hover:bg-slate-100"
                          >
                            &#9432;
                          </button>
                          {/*
                            No print button unless Accurate holds the document.
                            A failed receipt never reached it; a voided one has
                            had it deleted. Either way the GRN would be signed
                            for goods the ERP has no record of receiving, and
                            detail is the action those rows actually need —
                            it carries the reason.

                            An allowlist on `posted`, not a denylist on failed
                            and voided. The denylist let `pending` through: a
                            receipt still in flight — or stuck pending because
                            the process died after the ERP call — printed as an
                            ordinary signable GRN. A status this UI has never
                            heard of would have printed too.
                          */}
                          {r.status !== ReceiptStatus.Posted ? null : (
                            <button
                              onClick={() => setPrintId(r.id)}
                              title={t('history.printGrn')}
                              aria-label={t('history.printGrnFor', { number: r.grNumber })}
                              className="h-7 w-7 cursor-pointer rounded border border-blue-200 bg-blue-50 text-xs text-blue-600 hover:bg-blue-100"
                            >
                              ⎙
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {printId ? <GrnPrint receiptId={printId} onClose={() => setPrintId(null)} /> : null}
      {detailId ? (
        <ReceiptDetailModal
          receiptId={detailId}
          onClose={() => setDetailId(null)}
          onVoided={() => setReloadToken((n) => n + 1)}
        />
      ) : null}
    </div>
  );
}

function StatusPill({ status }: { status: ReceiptStatus }) {
  const t = useT();
  const s = RECEIPT_STATUS_LABEL[status];
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold text-white ${s.cls}`}>
      {t(s.key)}
    </span>
  );
}
